/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Task, ESignature } from '../../../../services/api/types'
import { getTask, cancelTask, cancelRATask } from '../../../../services/api/tasks'
import { toast } from '../../../../services/toast'
import { useEsignature } from '../../../../hooks/useEsignature'
import RcmEsignatureModal from '../../../../components/esignature/RcmEsignatureModal'

interface TaskCancelProps {
  task: any
  isRATask?: boolean
  onFinished?: () => void
  onRefreshTable?: () => void
  beforeCancel?: () => void
}

/**
 * Task cancellation component - Handles task cancellation
 * Supports both regular tasks and RA (Risk Assessment) tasks
 * Includes eSignature integration
 * Migrated from: lego/page/tasks/cancel/TaskCancel.jsx
 */
export default function TaskCancel({
  task,
  isRATask = false,
  onFinished,
  onRefreshTable,
  beforeCancel
}: TaskCancelProps) {
  const { t } = useTranslation()
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Prevent multiple executions
    if (hasInitialized.current) return
    hasInitialized.current = true
    const performCancel = async (taskData: Task) => {
      // Call beforeCancel callback if provided
      if (beforeCancel) {
        beforeCancel()
      }

      // Check if eSignature is required
      const needsESignature = isRATask
        ? (taskData as any).flags?.cancelRATaskESignatureRequired
        : (taskData as any).flags?.cancelESignatureRequired

      if (needsESignature) {
        // Show eSignature modal and wait for validation
        try {
          const validation = await signAction(
            'ADMIN_ACTION',
            isRATask ? 'ADMIN_ACTION_TASK_CANCEL_RA' : 'ADMIN_ACTION_TASK_CANCEL'
          )

          const isDSO = validation && validation.username && validation.password
          const isSSO = validation && validation.result && validation.result.esignature === 'verified'

          // Convert ValidationResult to ESignature format (adds required index signature)
          if (!isDSO && !isSSO) {
            console.error('E-signature was not provided or cancelled')
            toast.error('E-signature was not provided or cancelled')
            return
          }
          const esignature: Record<string, unknown> & { username?: string; password?: string; reason?: string } = {
            relayState: validation.relayState
          }
          await cancelTaskInternal(taskData, esignature)
        } catch (error) {
          // User cancelled eSignature or authentication failed
          console.log('eSignature cancelled or failed')
          return
        }
      } else {
        // No eSignature required, proceed directly
        await cancelTaskInternal(taskData)
      }
    }

    /**
     * Internal cancellation function - handles API call
     */
    const cancelTaskInternal = async (taskData: Task, validation?: ESignature): Promise<void> => {
      try {
        // Call appropriate API method based on task type
        if (isRATask) {
          await cancelRATask(String(taskData.id), validation)
        } else {
          await cancelTask(String(taskData.id), undefined, validation)
        }

        // Show success message
        toast.success(t('TASKS_CANCELLED_SUCCESS_MSG'))

        // Call callbacks
        if (onFinished) {
          onFinished()
        }

        if (onRefreshTable) {
          onRefreshTable()
        }
      } catch (error: any) {
        // Handle error
        const errorMessage = error.body?.message || error.message || t('TASKS_CANCELLED_FAIL_MSG')
        toast.error(errorMessage)
        throw error
      }
    }

    // Load task with flags if not already loaded
    const initializeCancel = async () => {
      if (!task) return

      if (!(task as any).flags) {
        // Load full task details with flags
        try {
          const fullTask = await getTask(String(task.id))
          await performCancel(fullTask)
        } catch (error) {
          console.error('Failed to load task details:', error)
        }
      } else {
        // Task already has flags, proceed with cancellation
        await performCancel(task)
      }
    }

    initializeCancel()
  }, [])

  // Render eSignature modal if needed
  return (
    <>
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}
